import { isNone, isSome, Maybe, noop } from '@crocodille/util';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileType } from '../forms/contact-form';
import { string as zString } from 'zod';

/**
 * Validator function for usage with form.
 */
export interface Validator<V, E = string> {
  (value: V): Maybe<E>;
  /**
   * Optional dependencies of the validator.
   * When any of these values changes. Validation on field will be re-run.
   *
   * ! Dependency array size must no change. (Same rules apply as react useEffect deps)
   */
  readonly deps?: readonly unknown[];
}

/**
 * A validator which validates that the value is an email.
 */
export const EMAIL_VALIDATOR = (value: Maybe<string>) =>
  isSome(value) && !zString().email().safeParse(value).success
    ? t('validation_error.email')
    : null;

/**
 * A validator which validates that the value is an email.
 * This validator is used for v2 forms.
 */
export const EMAIL_VALIDATOR_V2 = (value: Maybe<string>) =>
  isSome(value) && !zString().email().safeParse(value).success
    ? ('validation_error.email' as const)
    : null;

/**
 * Validator which validate that the value is not `null`, `undefined` or an empty string.
 */
export const REQUIRED_VALIDATOR = (value: unknown) =>
  isNone(value) ||
  value === '' ||
  (typeof value === 'string' && value.trim().length === 0)
    ? t('validation_error.required')
    : null;

/**
 * Validator which validates that the value is not `null`, `undefined` or an empty string.
 *
 * This validator is used for v2 forms.
 */
export const REQUIRED_VALIDATOR_V2 = (value: unknown) =>
  isNone(value) ||
  value === '' ||
  (typeof value === 'string' && value.trim().length === 0)
    ? ('validation_error.required' as const)
    : null;

/**
 * Validator which validates that the value is `true`.
 */
export const REQUIRED_TRUE_VALIDATOR = (value: unknown) =>
  value !== true ? ('validation_error.required_true' as const) : null;

/**
 * Validates postcodes
 */
export const POSTCODE_VALIDATOR = (value: unknown) =>
  isNone(value) ||
  value === '' ||
  (typeof value === 'string' && !/^\d{5}$/.test(value.trim()))
    ? ('validation_error.invalid_postcode' as const)
    : null;
/**
 * Create a validator function which can be used as form field validator with dependencies.
 */
export function useValidator<V, E extends string = string>(
  validate: (value: V) => Maybe<E>,
  deps: readonly unknown[],
): Validator<V, E> {
  const validateRef = useRef(validate);
  validateRef.current = validate;

  const validator: Validator<V, E> = useCallback(
    (value: V) => validateRef.current(value),
    [],
  );

  (validator as { deps: readonly unknown[] }).deps = deps;

  return validator;
}

export function useMaxLengthValidator(
  maxLength: number,
): Validator<Maybe<string>> {
  return useValidator(
    (value) =>
      isSome(value) &&
      value.trim().length > 0 &&
      value.trim().length > maxLength
        ? t('validation_error.max_length', { maxLength })
        : null,
    [maxLength],
  );
}

export function v2_useMaxLengthValidator(maxLength: number) {
  return useValidator(
    (value: Maybe<string>) =>
      isSome(value) &&
      value.trim().length > 0 &&
      value.trim().length > maxLength
        ? ('validation_error.max_length' as const)
        : null,
    [maxLength],
  );
}

export function useMinLengthValidator(
  minLength: number,
): Validator<Maybe<string>> {
  return useValidator(
    (value) =>
      isSome(value) &&
      value.trim().length > 0 &&
      value.trim().length < minLength
        ? t('validation_error.min_length', { minLength })
        : null,
    [minLength],
  );
}

export function v2_useMinLengthValidator(minLength: number) {
  return useValidator(
    (value: Maybe<string>) =>
      isSome(value) &&
      value.trim().length > 0 &&
      value.trim().length < minLength
        ? ('validation_error.min_length' as const)
        : null,
    [minLength],
  );
}

export function useEnumValidator<E extends string>(
  enumValues: readonly E[],
): Validator<E> {
  return useValidator(
    (value) =>
      isNone(value) || enumValues.includes(value)
        ? null
        : ('validation_error.enum' as const),
    [enumValues.join(',')],
  );
}

export function useMaxSizeValidator(maxSize: number) {
  return useValidator(
    (value: FileType[] | null) => {
      if (isNone(value) || value.length === 0) return null;

      const oversizedFile = value.some((file) => {
        const fileSize =
          file instanceof File
            ? file.size
            : file.data instanceof Blob
            ? file.data.size
            : 0;

        return fileSize >= maxSize * 1024 * 1024;
      });

      return oversizedFile ? ('validation_error.max_size' as const) : null;
    },
    [maxSize],
  );
}

/**
 * Initialization of a form field.
 */
export interface FormFieldInit<V, E extends string = string> {
  /**
   * Initial value of a field.
   */
  readonly value: V;
  /**
   * Whether the field is statically required (for html required atribute).
   *
   * ! When required, add the `REQUIRED_VALIDATOR` also.
   */
  readonly required?: boolean;
  /**
   * Mark field visually as disabled.
   */
  readonly disabled?: boolean;
  /**
   * A list of validator for the field.
   */
  readonly validators?: readonly Validator<V, E>[];
}

/**
 * Form field state.
 */
export interface FormField<V, E = string> {
  /**
   * Current value of a field.
   */
  readonly value: V;
  /**
   * Current error state of the field. The error is directly what's shown to user.
   */
  readonly error: Maybe<E>;
  /**
   * Whether the field has been touched by user.
   *
   * * Touched means that the field was focused and left.
   *
   */
  readonly touched: boolean;
  /**
   * Required flag for the field. Results in html required attribute in web.
   *
   * ! Field still required `REQUIRED_VALIDATOR`.
   */
  readonly required: boolean;
  /**
   * Mark field visually as disabled.
   */
  readonly disabled: boolean;
  /**
   * Handler for field value change.
   */
  onChange(value: V): void;
  /**
   * Should be triggered when user leaves the field.
   */
  onBlur(): void;
  /**
   * Used to manually validate a field.
   */
  validate(): Maybe<string>;
  /**
   * Used to re-set the field to either initial value (when given nothing) or
   * a specific value (when given a value).
   *
   * * Reset means that the field will be put back to untouched state.
   */
  reset(value?: V): void;
}

function runValidators<V, E>(
  validators: readonly Validator<V, E>[],
  value: V,
): Maybe<E> {
  for (const validator of validators) {
    const validatorError = validator(value);

    if (isSome(validatorError)) {
      return validatorError;
    }
  }

  return null;
}

export function useFormField<V, E extends string = never>(
  init: FormFieldInit<V, E>,
): FormField<V, E> {
  const [value, setValue] = useState(() => init.value);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null as Maybe<E>);

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const validate = useCallback(() => {
    const error = runValidators(init.validators ?? [], valueRef.current);

    setError(error);

    return error;
  }, []);

  const onBlur = useCallback(() => setTouched(true), []);
  const reset = useCallback((value?: V) => {
    setValue(value === undefined ? init.value : value);
    setTouched(false);
    setError(null);
  }, []);

  const validatorDeps = (init.validators ?? []).flatMap(
    (validator) => validator.deps ?? [],
  );

  useEffect(() => {
    if (touched) {
      validate();
    }
  }, [touched, value, ...validatorDeps]);

  const formField = {
    value,
    error: touched ? error : null,
    touched,
    required: init.required ?? false,
    disabled: init.disabled ?? false,
    onChange: setValue,
    onBlur,
    validate,
    reset,
  };

  return formField;
}

/**
 * Resolves into field value type.
 */
type FormFieldValueType<F> = F extends { value: infer V } ? V : never;

/**
 * Resolves into data object given an object of fields.
 */
type FormData<F extends Record<string, FormField<unknown, unknown>>> = {
  readonly [N in keyof F]: FormFieldValueType<F[N]>;
};

/**
 * Form initialization.
 */
export interface UseFormInit<
  F extends Record<string, FormField<unknown, unknown>>,
  E extends string = string,
> {
  /**
   * Member fields of the form.
   */
  readonly fields: F;
  /**
   * Triggered handler once the form is validated and submitted.
   */
  readonly onSubmit?: (data: FormData<F>) => void;
  /**
   * Validators to run on the whole form data before it's submitted.
   */
  readonly validators?: readonly Validator<FormData<F>, E>[];
}

/**
 * Additional (external) form state.
 */
export interface UseFormState<E extends string = string> {
  /**
   * External form error.
   * Will be used as if global form validator returned an error.
   */
  readonly formError?: Maybe<E>;
}

export interface Form<
  F extends Record<string, FormField<unknown, unknown>>,
  E extends string = string,
> {
  readonly fields: F;
  readonly error: Maybe<E>;
  handleSubmit(): boolean;
  reset(data?: Partial<FormData<F>>): void;
}

export function getFieldValues<
  F extends Record<string, FormField<unknown, unknown>>,
>(fields: F): FormData<F> {
  return Object.fromEntries(
    Object.entries(fields).map(([field, state]) => [field, state.value]),
  ) as unknown as FormData<F>;
}

/**
 * Create a form using given fields.
 */
export function useForm<
  F extends Record<string, FormField<unknown, unknown>>,
  E extends string = string,
>(
  { fields, onSubmit = noop, validators = [] }: UseFormInit<F, E>,
  { formError = null }: UseFormState<E> = {},
): Form<F, E> {
  const [error, setError] = useState(null as Maybe<E>);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const validatorsRef = useRef(validators);
  validatorsRef.current = validators;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const handleSubmit = useCallback(() => {
    for (const field of Object.values(fieldsRef.current)) {
      field.onBlur();
    }

    if (
      Object.values(fieldsRef.current).some((field) => isSome(field.validate()))
    ) {
      // * Some fields have errors.
      return false;
    }

    //
    // Collect field values into object.
    //
    const data = getFieldValues(fieldsRef.current);

    const error = runValidators(validatorsRef.current, data);
    setError(error);

    if (isSome(error)) {
      // * The form itself has errors.
      return false;
    }

    onSubmitRef.current(data);

    return true;
  }, []);

  const reset = useCallback((data: FormData<F>) => {
    for (const fieldName of Object.keys(fieldsRef.current)) {
      fieldsRef.current[fieldName].reset(data?.[fieldName]);
    }
  }, []);

  return {
    fields,
    error: formError ?? error,
    handleSubmit,
    reset,
  };
}
